import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { KrakenLogo } from "@/components/PublicLayout";
import {
  LayoutDashboard, FileText, Megaphone, CalendarDays, Wallet, User,
  HandHelping, Waves, Shield, LogOut, Home, Menu, X,
} from "lucide-react";

const portalLinks = [
  { to: "/portal", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/portal/announcements", label: "Announcements", icon: Megaphone },
  { to: "/portal/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/portal/documents", label: "Documents", icon: FileText },
  { to: "/portal/payments", label: "Payments", icon: Wallet },
  { to: "/portal/profile", label: "Profile", icon: User },
  { to: "/portal/volunteer", label: "Volunteer", icon: HandHelping },
  { to: "/portal/service", label: "Service Tracker", icon: Waves },
];

function SidebarNav({ user, isAdmin, onNavigate }) {
  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm whitespace-nowrap ${
      isActive ? "bg-[#00E5FF]/10 text-[#00E5FF] border-l-2 border-[#00E5FF]" : "text-slate-300 hover:text-white hover:bg-white/5"
    }`;
  return (
    <>
      <div className="px-3 py-2 mb-2">
        <div className="text-xs text-slate-400">Signed in as</div>
        <div className="text-sm font-semibold text-white truncate">{user?.name}</div>
        <div className="text-[10px] tracking-widest text-[#00E5FF] uppercase">{user?.role}</div>
      </div>
      <nav className="flex flex-col gap-1">
        {portalLinks.map((l) => (
          <NavLink key={l.to} to={l.to} end={l.end}
            data-testid={`portal-nav-${l.label.toLowerCase().replace(/ /g, "-")}`}
            className={linkCls} onClick={onNavigate}
          >
            <l.icon size={16} /> {l.label}
          </NavLink>
        ))}
        {isAdmin && (
          <>
            <div className="mt-3 pt-3 border-t border-white/10" />
            <NavLink to="/admin" end data-testid="portal-nav-admin" className={linkCls} onClick={onNavigate}>
              <Shield size={16} /> Admin Panel
            </NavLink>
            <NavLink to="/admin/accounts" data-testid="portal-nav-accounts" className={linkCls} onClick={onNavigate}>
              <User size={16} /> Accounts
            </NavLink>
          </>
        )}
      </nav>
    </>
  );
}

export default function PortalLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user && (user.role === "admin" || user.role === "coach");
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#050A15] text-white flex flex-col" data-testid="portal-layout">
      <header className="nav-glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-6 py-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={() => setMenuOpen(true)}
              className="lg:hidden p-2 -ml-1 text-slate-300 hover:text-white shrink-0"
              aria-label="Open menu"
              data-testid="open-menu"
            >
              <Menu size={20} />
            </button>
            <Link to="/portal" className="min-w-0"><KrakenLogo /></Link>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Link to="/" className="hidden sm:inline-flex btn-ghost text-sm" data-testid="back-to-site">
              <Home size={16} /> Public Site
            </Link>
            <button
              onClick={handleLogout}
              className="btn-ghost text-xs sm:text-sm px-3"
              data-testid="logout-btn"
            >
              <LogOut size={14} className="sm:hidden" />
              <LogOut size={16} className="hidden sm:inline" />
              <span className="hidden xs:inline sm:inline">Sign Out</span>
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl w-full mx-auto px-3 sm:px-6 py-4 sm:py-6 grid lg:grid-cols-[240px_1fr] gap-4 lg:gap-6">
        <aside className="hidden lg:block kr-card rounded-xl p-3 h-fit lg:sticky lg:top-24" data-testid="portal-sidebar">
          <SidebarNav user={user} isAdmin={isAdmin} />
        </aside>
        <section data-testid="portal-content" className="min-w-0">{children}</section>
      </div>

      {/* Mobile drawer */}
      {menuOpen && (
        <div
          className="fixed inset-0 z-50 lg:hidden bg-black/70 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
          data-testid="mobile-menu"
        >
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute inset-y-0 left-0 w-[85%] max-w-[300px] bg-[#0A0F1E] border-r border-white/10 p-4 overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-4">
              <KrakenLogo />
              <button
                onClick={() => setMenuOpen(false)}
                className="p-2 text-slate-300 hover:text-white"
                aria-label="Close menu"
                data-testid="close-menu"
              >
                <X size={20} />
              </button>
            </div>
            <SidebarNav user={user} isAdmin={isAdmin} onNavigate={() => setMenuOpen(false)} />
            <div className="mt-4 pt-4 border-t border-white/10 flex flex-col gap-2">
              <Link to="/" onClick={() => setMenuOpen(false)} className="btn-ghost text-sm justify-center">
                <Home size={16} /> Public Site
              </Link>
              <button onClick={handleLogout} className="btn-ghost text-sm justify-center text-red-300">
                <LogOut size={16} /> Sign Out
              </button>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
